// src/components/ErrorBoundary.js
import React from 'react';
import './ErrorBoundary.css';

/**
 * ErrorBoundary — Capture les erreurs React non gérées.
 * Empêche l'app de crasher complètement.
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error('🛑 [ErrorBoundary]', error, errorInfo);

        this.setState({ error, errorInfo });

        // Log vers le backend (non bloquant)
        try {
            const token = localStorage.getItem('token');
            fetch('https://api.insulated.pro/api/logs/frontend-error', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({
                    message: error?.message || 'Unknown error',
                    stack: error?.stack,
                    componentStack: errorInfo?.componentStack,
                    url: window.location.href,
                    userAgent: navigator.userAgent,
                    timestamp: new Date().toISOString(),
                }),
            }).catch(() => {
                // Silencieux : on ne veut pas crasher à cause du log
            });
        } catch (_) {
            // Silencieux
        }
    }

    handleReload = () => {
        window.location.href = '/dashboard';
    };

    handleRetry = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="errorboundary-page">
                    <div className="errorboundary-card">
                        <div className="errorboundary-icon">💥</div>
                        <h1 className="errorboundary-title">
                            Oups, quelque chose s'est mal passé
                        </h1>
                        <p className="errorboundary-subtitle">
                            Pas de panique, votre compte et vos données sont
                            en sécurité. Vous pouvez réessayer ou revenir au
                            tableau de bord.
                        </p>

                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details className="errorboundary-details">
                                <summary>Détails techniques</summary>
                                <pre>{this.state.error.toString()}</pre>
                                <pre>{this.state.errorInfo?.componentStack}</pre>
                            </details>
                        )}

                        <div className="errorboundary-actions">
                            <button
                                className="errorboundary-btn errorboundary-btn-primary"
                                onClick={this.handleRetry}
                            >
                                Réessayer
                            </button>
                            <button
                                className="errorboundary-btn errorboundary-btn-secondary"
                                onClick={this.handleReload}
                            >
                                Retour au dashboard
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;